import { useCallback, useEffect, useState } from 'react'
import { useEvent } from './useEvent'
import anylogger from '@app/anylogger'

const log = anylogger('useUndo')

export interface UndoPos {
	start: number
	end: number
}
export interface UndoEntry {
	value: string
	caretPos: UndoPos
}
export interface UseUndoResult {
	saveState: (value: string, caretPos: UndoPos) => void
	getUndoValue: () => UndoEntry | undefined
	getRedoValue: () => UndoEntry | undefined
	hasUndo: boolean
	hasRedo: boolean
	isInitialized: boolean
}
export function useUndo(): UseUndoResult {
	const [undoIndex, setUndoIndex] = useState(-1)
	const [undoStack, setUndoStack] = useState([] as UndoEntry[])

	const saveState = useCallback(
		(value: string, caretPos: UndoPos) => {
			// Clear any "redo" states if a new change is made after an undo
			if (undoIndex < undoStack.length - 1) {
				undoStack.splice(undoIndex + 1)
			}
			undoStack.push({ value, caretPos })
			setUndoStack(undoStack)
			setUndoIndex(undoStack.length - 1)
		},
		[undoIndex, undoStack]
	)

	const undo = useCallback(() => {
		let value: UndoEntry | undefined = undefined
		let idx = undoIndex
		if (idx > 0) {
			idx = idx - 1
			value = undoStack[idx]
			setUndoIndex(idx)
		}

		return value
	}, [undoIndex, undoStack])

	const redo = useCallback(() => {
		let value = undefined
		let idx = undoIndex
		if (idx < undoStack.length - 1) {
			idx = idx + 1
			value = undoStack[idx]
			setUndoIndex(idx)
		}
		return value
	}, [undoIndex, undoStack])
	return {
		saveState,
		getUndoValue: undo,
		getRedoValue: redo,
		hasUndo: undoIndex > 0,
		hasRedo: undoIndex < undoStack.length - 1,
		isInitialized: undoIndex >= 0
	}
}
